// frontend_react/src/components/AdminPanel/CreateUserPanel.jsx

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { AdminButton } from "../../pages/admin/action_engine";
import { checkCompanyExists } from "../../services/adminApi";


import {
  AdminFormSection,
  AdminSectionBadge,
  AdminSectionHeader,
} from "../../pages/admin/section_engine";

export default function CreateUserForm({ initialData = {}, onSubmit, variant = "page" }) {
  const [formData, setFormData] = useState({
    username: "",
    role: "serveur",
    email: "",
    firstname: "",
    lastname: "",
    company_name: "",
    phone_number: "",
    gender: "",
    address: "",
    postal_code: "",
    city: "",
    country: "France",
    social_security_number: "",
    date_of_birth: "",
    siret: "",
    comp_phone: "",
    address_line1: "",
    address_line2: "",
    company_postal_code: "",
    company_city: "",
    company_country: "France",
    ...initialData,
  });

  const [localMessage, setLocalMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [companyExists, setCompanyExists] = useState(null);

  useEffect(() => {
    const name = formData.company_name.trim();

    if (!name) {
      setCompanyExists(null);
      return;
    }

    const controller = new AbortController();

    checkCompanyExists(name, controller.signal)
      .then((res) => setCompanyExists(res.data.exists))
      .catch(() => setCompanyExists(null));

    return () => controller.abort();
  }, [formData.company_name]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateRequiredFields = () => {
    const requiredFields = [
      "username",
      "role",
      "email",
      "firstname",
      "lastname",
      "phone_number",
      "gender",
      "address",
      "postal_code",
      "city",
      "country",
      "social_security_number",
      "date_of_birth",
    ];

    for (const field of requiredFields) {
      if (!formData[field]?.toString().trim()) {
        setLocalMessage(`Le champ “${field}” est obligatoire.`);
        setIsError(true);
        return false;
      }
    }

    if (formData.company_name.trim() && companyExists === false) {
      const companyRequiredFields = [
        "siret",
        "address_line1",
        "company_postal_code",
        "company_city",
        "company_country",
      ];

      for (const field of companyRequiredFields) {
        if (!formData[field]?.toString().trim()) {
          setLocalMessage(
            `La société n’existe pas : le champ “${field}” est obligatoire pour la créer.`
          );
          setIsError(true);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmitForm = (event) => {
    event.preventDefault();

    if (!validateRequiredFields()) return;

    setLocalMessage("");
    setIsError(false);
    onSubmit(formData);
  };

  return (
    <FormWrapper $variant={variant} onSubmit={handleSubmitForm}>
      {localMessage && (
        <PanelMessage $error={isError}>
          {localMessage}
        </PanelMessage>
      )}

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Compte</AdminSectionBadge>
          <h4>Identifiants & rôle</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field>
            <label htmlFor="username">Nom d’utilisateur *</label>
            <input
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="email">E-mail *</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="role">Rôle *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="serveur">Serveur</option>
              <option value="vendeur">Vendeur</option>
              <option value="user">Utilisateur</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Identité</AdminSectionBadge>
          <h4>Informations personnelles</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field>
            <label htmlFor="firstname">Prénom *</label>
            <input
              id="firstname"
              type="text"
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="lastname">Nom *</label>
            <input
              id="lastname"
              type="text"
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="gender">Genre *</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Sélectionnez</option>
              <option value="male">Homme</option>
              <option value="female">Femme</option>
              <option value="other">Autre</option>
            </select>
          </Field>

          <Field>
            <label htmlFor="date_of_birth">Date de naissance *</label>
            <input
              id="date_of_birth"
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              required
            />
          </Field>

          <Field $full>
            <label htmlFor="social_security_number">
              N° de sécurité sociale *
            </label>
            <input
              id="social_security_number"
              type="text"
              name="social_security_number"
              value={formData.social_security_number}
              onChange={handleChange}
              required
            />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Coordonnées</AdminSectionBadge>
          <h4>Adresse & contact</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field>
            <label htmlFor="phone_number">Téléphone utilisateur *</label>
            <input
              id="phone_number"
              type="tel"
              name="phone_number"
              placeholder="+33..."
              value={formData.phone_number}
              onChange={handleChange}
              required
            />
          </Field>

          <Field $full>
            <label htmlFor="address">Adresse utilisateur *</label>
            <input
              id="address"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="postal_code">Code postal *</label>
            <input
              id="postal_code"
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="city">Ville *</label>
            <input
              id="city"
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label htmlFor="country">Pays *</label>
            <input
              id="country"
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
            />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Société</AdminSectionBadge>
          <h4>Rattachement optionnel</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field $full>
            <label htmlFor="company_name">Nom de la société</label>
            <input
              id="company_name"
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder="Laisser vide si pas de société"
            />
          </Field>
        </FieldsGrid>

        {companyExists === true && (
          <InlineInfo>
            Société existante → aucun champ complémentaire requis.
          </InlineInfo>
        )}

        {companyExists === false && (
          <>
            <InlineInfo>
              Société introuvable → renseigner les champs ci-dessous pour la créer.
            </InlineInfo>

            <FieldsGrid>
              <Field>
                <label htmlFor="siret">SIRET *</label>
                <input
                  id="siret"
                  type="text"
                  name="siret"
                  value={formData.siret}
                  onChange={handleChange}
                  required
                />
              </Field>

              <Field>
                <label htmlFor="comp_phone">Téléphone société</label>
                <input
                  id="comp_phone"
                  type="tel"
                  name="comp_phone"
                  value={formData.comp_phone}
                  onChange={handleChange}
                />
              </Field>

              <Field $full>
                <label htmlFor="address_line1">Adresse ligne 1 *</label>
                <input
                  id="address_line1"
                  type="text"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  required
                />
              </Field>

              <Field $full>
                <label htmlFor="address_line2">Adresse ligne 2</label>
                <input
                  id="address_line2"
                  type="text"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleChange}
                />
              </Field>

              <Field>
                <label htmlFor="company_postal_code">Code postal *</label>
                <input
                  id="company_postal_code"
                  type="text"
                  name="company_postal_code"
                  value={formData.company_postal_code}
                  onChange={handleChange}
                  required
                />
              </Field>

              <Field>
                <label htmlFor="company_city">Ville *</label>
                <input
                  id="company_city"
                  type="text"
                  name="company_city"
                  value={formData.company_city}
                  onChange={handleChange}
                  required
                />
              </Field>

              <Field>
                <label htmlFor="company_country">Pays *</label>
                <input
                  id="company_country"
                  type="text"
                  name="company_country"
                  value={formData.company_country}
                  onChange={handleChange}
                  required
                />
              </Field>
            </FieldsGrid>
          </>
        )}
      </AdminFormSection>

      <AdminButton
        type="submit"
        variant="success"
        fullWidth
      >
        Créer l’utilisateur
      </AdminButton>
    </FormWrapper>
  );
}

const FormWrapper = styled.form`
  display: grid;
  gap: 0.95rem;
  color: #f7f7f7;

  ${({ $variant }) =>
    $variant === "sidePanel" &&
    `
      padding: 0;
      background: transparent;
    `}
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  select {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus,
  select:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }
`;

const InlineInfo = styled.div`
  margin: 0.9rem 0;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.85rem;
  font-weight: 750;
  line-height: 1.4;
`;

const PanelMessage = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;